// src/App.js
import React, { useState, useEffect } from "react";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  onSnapshot,
  addDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
  getDocs,
  writeBatch
} from "firebase/firestore";
import { motion } from "framer-motion";
import bingoHeader from "./assets/bingo-header.png";
import dancingLady from "./assets/dancing-lady-icon.png";
import { defaultLineDances } from "./data/defaultLineDances";
import AccessCodeGate from "./components/AccessCodeGate";
import "./styles.css";

// ——— Firebase init ———
const firebaseConfig = {
  apiKey:"AIzaSyAOMMR6s1yUcIa-flCMXKPCXlZETjRvBOQ",
  authDomain: "linedance-bingo-1bdb0.firebaseapp.com",
  projectId: "linedance-bingo-1bdb0",
  storageBucket: "linedance-bingo-1bdb0.firebasestorage.app",
  messagingSenderId: "55305705770",
  appId: "1:55305705770:web:76989f5aa57b7b31066653"
};
initializeApp(firebaseConfig);
const db = getFirestore();

// ——— Dance list + helpers ———
const dances = defaultLineDances.map((d) => ({
  name: d.name,
  creator: d.creator || ""
}));

const shuffle = (arr) => [...arr].sort(() => 0.5 - Math.random());

// 4-character Game ID (easy to read aloud)
const makeGameId = () =>
  Math.random().toString(36).substring(2, 6).toUpperCase();

const makeCard = () => {
  const deck = shuffle(dances).slice(0, 25);
  return Array.from({ length: 5 }, (_, i) => deck.slice(i * 5, (i + 1) * 5));
};

// ——— Host dashboard ———
function HostDashboard({ gameId }) {
  const [players, setPlayers] = useState([]);
  const [called, setCalled] = useState([]);
  const [winner, setWinner] = useState(null);

  useEffect(() => {
    if (!gameId) return;

    const unsubP = onSnapshot(
      collection(db, "games", gameId, "players"),
      (snap) => setPlayers(snap.docs.map((d) => d.data().playerName))
    );

    const unsubC = onSnapshot(
      query(
        collection(db, "games", gameId, "calledDances"),
        orderBy("ts", "desc")
      ),
      (snap) => setCalled(snap.docs.map((d) => d.data().dance))
    );

    const unsubW = onSnapshot(doc(db, "games", gameId), (snap) =>
      setWinner(snap.data()?.winner || null)
    );

    return () => {
      unsubP();
      unsubC();
      unsubW();
    };
  }, [gameId]);

  const callNext = async () => {
    if (winner) return;

    const remaining = dances.filter((d) => !called.includes(d.name));
    if (!remaining.length) {
      alert("All dances called!");
      return;
    }

    const pick = remaining[Math.floor(Math.random() * remaining.length)];

    await addDoc(collection(db, "games", gameId, "calledDances"), {
      dance: pick.name,
      ts: serverTimestamp()
    });
  };

  // ✅ UPDATED RESET: also increments resetCounter (broadcast reset to players)
  const resetGame = async () => {
    try {
      console.log("RESET → starting reset for", gameId);

      const gameRef = doc(db, "games", gameId);
      const gameSnap = await getDoc(gameRef);
      const currentReset = gameSnap.data()?.resetCounter || 0;

      // 1) clear winner + increment resetCounter
      await setDoc(
        gameRef,
        { winner: null, resetCounter: currentReset + 1 },
        { merge: true }
      );

      // 2) delete called dances
      const calledRef = collection(db, "games", gameId, "calledDances");
      const calledSnap = await getDocs(calledRef);

      const batch = writeBatch(db);
      calledSnap.forEach((d) => batch.delete(d.ref));
      await batch.commit();

      alert("Game reset: winner cleared + called dances wiped.");
      console.log("RESET → done");
    } catch (err) {
      console.error("RESET → failed", err);
      alert("Reset failed. Check console for details.");
    }
  };

  return (
    <div className="host-dashboard">
      <h2>Host ID: {gameId}</h2>

      <button onClick={callNext} disabled={!!winner}>
        Call Random Dance
      </button>
      <button onClick={resetGame}>Reset Game</button>

      <h3>Players Joined:</h3>
      <ul>{players.map((n, i) => <li key={i}>{n}</li>)}</ul>

      <h3>Called (newest first):</h3>
      <ul>{called.map((d, i) => <li key={i}>{d}</li>)}</ul>

      {winner && (
        <div className="winner-banner">
          <img src={dancingLady} alt="💃" /> <strong>{winner}</strong> WINS!{" "}
          <img src={dancingLady} alt="💃" />
        </div>
      )}
    </div>
  );
}

// ——— Player board ———
function BingoBoard({ gameId, playerName, card }) {
  const [marked, setMarked] = useState(
    Array(5).fill(null).map(() => Array(5).fill(false))
  );
  const [called, setCalled] = useState([]);
  const [winner, setWinner] = useState(null);

  // ✅ NEW: listen to resetCounter so we can clear marks on reset
  const [resetCounter, setResetCounter] = useState(0);

  useEffect(() => {
    if (!gameId) return;

    const unsubC = onSnapshot(
      query(
        collection(db, "games", gameId, "calledDances"),
        orderBy("ts", "asc")
      ),
      (snap) => setCalled(snap.docs.map((d) => d.data().dance))
    );

    // ✅ UPDATED: also capture resetCounter from the game doc
    const unsubW = onSnapshot(doc(db, "games", gameId), (snap) => {
      const data = snap.data() || {};
      setWinner(data.winner || null);
      setResetCounter(data.resetCounter || 0);
    });

    return () => {
      unsubC();
      unsubW();
    };
  }, [gameId]);

  // ✅ OPTION 1: keep same card, just clear marks whenever resetCounter changes
  useEffect(() => {
    setMarked(Array(5).fill(null).map(() => Array(5).fill(false)));
  }, [resetCounter]);

  const toggle = (r, c) => {
    if (winner) return;

    const tile = card[r][c];
    const danceName = tile?.name;

    if (!danceName) return;
    if (!called.includes(danceName)) return;

    const next = marked.map((row, i) =>
      row.map((v, j) => (i === r && j === c ? !v : v))
    );

    setMarked(next);

    const bingo =
      next.some((rw) => rw.every(Boolean)) ||
      next[0].map((_, ci) => next.every((rw) => rw[ci])).some(Boolean) ||
      [...Array(5)].every((_, i) => next[i][i]) ||
      [...Array(5)].every((_, i) => next[i][4 - i]);

    if (bingo) {
      updateDoc(doc(db, "games", gameId), { winner: playerName });
    }
  };

  return (
    <div className="bingo-board">
      <h2>{playerName}'s Card</h2>

      {winner && (
        <div className="winner-banner">
          <img src={dancingLady} alt="💃" /> BINGO! <strong>{winner}</strong>{" "}
          wins! <img src={dancingLady} alt="💃" />
        </div>
      )}

      <div className="grid">
        {card.map((row, r) =>
          row.map((tile, c) => (
            <motion.div key={`${r}-${c}`} whileTap={{ scale: 0.9 }}>
              <div
                className={`bingo-tile ${marked[r][c] ? "marked" : ""}`}
                onClick={() => toggle(r, c)}
              >
                <div className="tile-name">{tile.name}</div>
                {tile.creator ? (
                  <div className="tile-creator">{tile.creator}</div>
                ) : null}
              </div>
            </motion.div>
          ))
        )}
      </div>

      <h3>Called Dances:</h3>
      <ul>{called.map((d, i) => <li key={i}>{d}</li>)}</ul>
    </div>
  );
}

// ——— Main App ———
export default function App() {
  const [view, setView] = useState("join"); // "join"|"hostGate"|"host"|"play"
  const [gameId, setGameId] = useState("");
  const [playerName, setPlayerName] = useState("");
  const [playerId] = useState(() =>
    Math.random().toString(36).substring(2, 8).toUpperCase()
  );
  const [card, setCard] = useState([]);

  // Host access state
  const [hostUnlocked, setHostUnlocked] = useState(false);
  const [hostAccess, setHostAccess] = useState(null); // { code, data }

  useEffect(() => {
    // Persist host unlock across refresh
    const savedCode = localStorage.getItem("OTB_HOST_CODE");
    if (savedCode) {
      setHostUnlocked(true);
      setHostAccess({
        code: savedCode,
        data: { product: localStorage.getItem("OTB_HOST_PRODUCT") }
      });
    }
  }, []);

  const createNewGameAsHost = async () => {
    // Gate: must have host access code
    if (!hostUnlocked || !hostAccess?.code) {
      setView("hostGate");
      return;
    }

    const id = makeGameId();

    await setDoc(
      doc(db, "games", id),
      {
        winner: null,
        createdAt: serverTimestamp(),
        hostCode: hostAccess.code,
        resetCounter: 0 // ✅ initialize resetCounter
      },
      { merge: true }
    );

    setGameId(id);
    setView("host");
  };

  const joinGame = async () => {
    const id = gameId.trim().toUpperCase();
    if (!id || !playerName.trim()) return alert("Enter both Game ID and your name");

    const snap = await getDoc(doc(db, "games", id));
    if (!snap.exists()) return alert("Game ID not found");

    setCard(makeCard());

    await setDoc(doc(db, "games", id, "players", playerId), { playerName });

    setGameId(id);
    setView("play");
  };

  const logoutHost = () => {
    localStorage.removeItem("OTB_HOST_CODE");
    localStorage.removeItem("OTB_HOST_PRODUCT");
    setHostUnlocked(false);
    setHostAccess(null);
    setGameId("");
    setView("join");
  };

  return (
    <div className="app-container">
      <img src={bingoHeader} alt="Linedance Bingo" className="header-image" />

      {view === "join" && (
        <div className="join-screen">
          {!hostUnlocked ? (
            <button onClick={() => setView("hostGate")}>
              Host Login (Enter Access Code)
            </button>
          ) : (
            <>
              <div style={{ marginBottom: 10 }}>
                <strong>Host unlocked</strong> ✅ ({hostAccess?.data?.product || "Host"})
              </div>
              <button onClick={createNewGameAsHost}>Create New Game</button>
              <button onClick={logoutHost} style={{ marginTop: 8 }}>
                Log out host
              </button>
            </>
          )}

          <hr />

          <input
            placeholder="Game ID"
            value={gameId}
            onChange={(e) => setGameId(e.target.value)}
          />
          <input
            placeholder="Your Name"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
          />
          <button onClick={joinGame}>Join Game</button>
        </div>
      )}

      {view === "hostGate" && (
        <AccessCodeGate
          db={db}
          onUnlocked={(payload) => {
            setHostAccess(payload);
            setHostUnlocked(true);
            setView("join"); // go back to join screen where "Create New Game" appears
          }}
        />
      )}

      {view === "host" && gameId && <HostDashboard gameId={gameId} />}

      {view === "play" && gameId && (
        <BingoBoard gameId={gameId} playerName={playerName} card={card} />
      )}
    </div>
  );
}
